// src/ShoppingBudgetsPage.js
//
// Operator view of shopping budgets: what is held, what was spent, what was
// captured, and the ability to intervene.
//
// This page exists because the feature shipped without one. Authorization holds
// sit on customers' cards, stylists file receipts, money is captured and
// reimbursed — and until now nobody could look any of it up. The first support
// call about an unexpected hold had no answer anywhere in this console.
//
// Every figure here is read, never computed locally. `shopping_budget_totals`
// derives spend from the receipts on file; recalculating it in JavaScript would
// produce a second number that drifts from the one the money follows.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, IconButton, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Stack, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  CircularProgress, Grid, Tooltip, InputAdornment, Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { supabase } from './supabaseClient';
import { brand, ink, surface, radius } from './designTokens';

const money = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BUDGET_STATUS = {
  awaiting_authorization: { label: 'Awaiting card', color: 'default' },
  authorized:             { label: 'Active',       color: 'success' },
  capturing:              { label: 'Capturing',    color: 'warning' },
  captured:               { label: 'Completed',    color: 'info' },
  released:               { label: 'Released',     color: 'default' },
  expired:                { label: 'Expired',      color: 'default' },
  frozen:                 { label: 'Frozen',       color: 'error' },
  disputed:               { label: 'Disputed',     color: 'error' },
};

const RECEIPT_STATUS = {
  submitted: { label: 'Awaiting review', color: 'warning' },
  approved:  { label: 'Approved',        color: 'success' },
  rejected:  { label: 'Rejected',        color: 'error' },
  refunded:  { label: 'Refunded',        color: 'info' },
};

export default function ShoppingBudgetsPage() {
  const [budgets, setBudgets]   = useState([]);
  const [totals, setTotals]     = useState({});   // budget id -> totals row
  const [receipts, setReceipts] = useState({});   // budget id -> receipt rows
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy]         = useState(false);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [action, setAction]     = useState(null);  // {kind, budget?, receipt?}
  const [reason, setReason]     = useState('');

  const notify = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('shopping_budgets')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);

    if (error) {
      notify(`Could not load budgets: ${error.message}`, 'error');
      setLoading(false);
      return;
    }
    setBudgets(data || []);

    // Totals come from the database function that the capture itself uses.
    const entries = await Promise.all((data || []).map(async (b) => {
      const { data: rows } = await supabase.rpc('shopping_budget_totals', { p_budget_id: b.id });
      return [b.id, Array.isArray(rows) ? rows[0] : rows];
    }));
    setTotals(Object.fromEntries(entries));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadReceipts = async (budgetId) => {
    if (receipts[budgetId]) return;
    const { data } = await supabase
      .from('shopping_receipts')
      .select('*')
      .eq('budget_id', budgetId)
      .order('purchased_at', { ascending: false });
    setReceipts((r) => ({ ...r, [budgetId]: data || [] }));
  };

  const toggle = (budgetId) => {
    const next = expanded === budgetId ? null : budgetId;
    setExpanded(next);
    if (next) loadReceipts(next);
  };

  const runAction = async () => {
    const { kind, budget, receipt } = action;
    const needsReason = kind === 'freeze' || kind === 'reject_receipt';
    if (needsReason && !reason.trim()) {
      notify('A reason is required', 'error');
      return;
    }
    setBusy(true);
    const { error } = await supabase.functions.invoke('admin-shopping-budget-action', {
      body: {
        action: kind,
        budget_id: budget?.id,
        receipt_id: receipt?.id,
        reason: reason.trim(),
      },
    });
    setBusy(false);
    setAction(null);
    setReason('');
    if (error) { notify(`Failed: ${error.message}`, 'error'); return; }
    notify('Done', 'success');

    // Drop the cached receipts for that budget so the new status is read back
    // rather than patched locally into something that may not match.
    const budgetId = budget?.id || receipt?.budget_id;
    setReceipts((r) => { const next = { ...r }; delete next[budgetId]; return next; });
    if (expanded === budgetId) loadReceipts(budgetId);
    load();
  };

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return budgets.filter((b) => {
      const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
      const matchesTerm = !term
        || (b.client_phone_number || '').toLowerCase().includes(term)
        || (b.stylist_phone_number || '').toLowerCase().includes(term)
        || (b.id || '').toLowerCase().includes(term);
      return matchesStatus && matchesTerm;
    });
  }, [budgets, search, statusFilter]);

  // Headline figures. "On hold" is what customers' cards are currently
  // committed to — the number to know before anything else.
  const summary = useMemo(() => {
    const live = budgets.filter((b) => ['authorized', 'capturing'].includes(b.status));
    const onHold = live.reduce((sum, b) => sum + Number(b.authorized_amount || 0), 0);
    const captured = budgets
      .filter((b) => b.status === 'captured')
      .reduce((sum, b) => sum + Number(b.captured_amount || 0), 0);
    const awaitingReview = Object.values(receipts).flat()
      .filter((r) => r.status === 'submitted').length;
    return {
      onHold, captured, live: live.length,
      frozen: budgets.filter((b) => b.status === 'frozen').length,
      awaitingReview,
    };
  }, [budgets, receipts]);

  const stat = (label, value, tone) => (
    <Grid item xs={6} md={3}>
      <Box sx={{
        border: `1px solid ${surface.line}`, borderRadius: `${radius.md}px`,
        p: 2, height: '100%', backgroundColor: surface.card,
      }}>
        <Typography variant="caption" sx={{ color: ink[500] }}>{label}</Typography>
        <Typography variant="h5" sx={{ color: tone || ink[900], fontWeight: 700 }}>
          {value}
        </Typography>
      </Box>
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stat('Held on cards now', money(summary.onHold), brand.gold)}
        {stat('Active budgets', summary.live)}
        {stat('Captured to date', money(summary.captured))}
        {stat('Frozen', summary.frozen, summary.frozen ? '#B3261E' : undefined)}
      </Grid>

      <Card sx={{ borderRadius: `${radius.lg}px`, backgroundColor: surface.card }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2} sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" sx={{ color: ink[900] }}>Shopping budgets</Typography>
              <Typography variant="body2" sx={{ color: ink[500] }}>
                Money held on a client's card for a stylist to spend during one appointment
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small" placeholder="Phone or budget id" value={search}
                onChange={(e) => setSearch(e.target.value)} sx={{ width: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: ink[500] }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select size="small" value={statusFilter} sx={{ width: 170 }}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All statuses</MenuItem>
                {Object.entries(BUDGET_STATUS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Stack>

          {loading ? <CircularProgress size={24} /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>Client</TableCell>
                  <TableCell>Stylist</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Authorized</TableCell>
                  <TableCell align="right">Spent</TableCell>
                  <TableCell align="right">Left</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" sx={{ color: ink[500] }}>
                        {budgets.length === 0
                          ? 'No shopping budgets yet.'
                          : 'Nothing matches that search.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((b) => {
                  const t = totals[b.id] || {};
                  const meta = BUDGET_STATUS[b.status] || { label: b.status, color: 'default' };
                  const isOpen = expanded === b.id;
                  const rows = receipts[b.id] || [];
                  return (
                    <React.Fragment key={b.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggle(b.id)}>
                            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{b.client_phone_number}</Typography>
                          <Typography variant="caption" sx={{ color: ink[500] }}>
                            {new Date(b.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{b.stylist_phone_number}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={meta.label} color={meta.color} />
                          {b.frozen_reason && (
                            <Tooltip title={b.frozen_reason}>
                              <Typography variant="caption" sx={{ display: 'block', color: '#B3261E' }}>
                                {b.frozen_reason.slice(0, 28)}
                                {b.frozen_reason.length > 28 ? '…' : ''}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right">{money(b.authorized_amount)}</TableCell>
                        <TableCell align="right">{money(t.receipted)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {money(t.remaining)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {b.status === 'authorized' && (
                            <Tooltip title="Stop further spending while a problem is looked at">
                              <IconButton size="small" onClick={() => setAction({ kind: 'freeze', budget: b })}>
                                <AcUnitIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {b.status === 'frozen' && (
                            <Tooltip title="Allow spending again">
                              <IconButton size="small" onClick={() => setAction({ kind: 'unfreeze', budget: b })}>
                                <LockOpenIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }}>
                          <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ py: 2, pl: 4 }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <ReceiptLongIcon fontSize="small" sx={{ color: ink[500] }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: ink[900] }}>
                                  Receipts
                                </Typography>
                              </Stack>
                              {rows.length === 0 ? (
                                <Typography variant="body2" sx={{ color: ink[500] }}>
                                  Nothing filed against this budget yet.
                                </Typography>
                              ) : rows.map((r) => {
                                const rm = RECEIPT_STATUS[r.status] || { label: r.status, color: 'default' };
                                return (
                                  <Box key={r.id} sx={{
                                    border: `1px solid ${surface.line}`,
                                    borderRadius: `${radius.md}px`,
                                    p: 1.5, mb: 1,
                                    display: 'flex', alignItems: 'center', gap: 2,
                                  }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {r.merchant_name} · {money(r.amount)}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: ink[500] }}>
                                        {new Date(r.purchased_at).toLocaleString()}
                                        {r.stylist_notes ? ` · ${r.stylist_notes}` : ''}
                                      </Typography>
                                    </Box>
                                    <Chip size="small" label={rm.label} color={rm.color} />
                                    {r.status === 'submitted' && (
                                      <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Approve this purchase">
                                          <IconButton
                                            size="small" color="success"
                                            onClick={() => setAction({ kind: 'approve_receipt', receipt: r })}
                                          >
                                            <CheckCircleOutlineIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Reject — it will not be captured">
                                          <IconButton
                                            size="small" color="error"
                                            onClick={() => setAction({ kind: 'reject_receipt', receipt: r })}
                                          >
                                            <CancelOutlinedIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!action} onClose={() => !busy && setAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {action?.kind === 'freeze' && 'Freeze this budget?'}
          {action?.kind === 'unfreeze' && 'Allow spending again?'}
          {action?.kind === 'approve_receipt' && 'Approve this receipt?'}
          {action?.kind === 'reject_receipt' && 'Reject this receipt?'}
        </DialogTitle>
        <DialogContent>
          {action?.kind === 'freeze' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The stylist cannot record further purchases. The hold stays on the
              client's card — this does not release their money.
            </Alert>
          )}
          {action?.kind === 'reject_receipt' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              A rejected receipt is not captured, so the stylist is not reimbursed
              for it.
            </Alert>
          )}
          {(action?.kind === 'freeze' || action?.kind === 'reject_receipt') && (
            <TextField
              label="Reason" value={reason} onChange={(e) => setReason(e.target.value)}
              fullWidth size="small" multiline minRows={2} autoFocus
              helperText="Recorded in the ledger and visible in support history"
            />
          )}
          {action?.kind === 'approve_receipt' && (
            <Typography variant="body2">
              {action.receipt?.merchant_name} · {money(action.receipt?.amount)} will be
              included when this budget is captured.
            </Typography>
          )}
          {action?.kind === 'unfreeze' && (
            <Typography variant="body2">
              The stylist will be able to record purchases against this budget again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAction(null); setReason(''); }} disabled={busy}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={runAction}
            sx={{ backgroundColor: brand.gold, '&:hover': { backgroundColor: brand.goldDeep } }}
          >
            {busy ? 'Working…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open} autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
